import { fabric } from "fabric";
import polygonClipping from "polygon-clipping";
import { makeFabricShape } from "./shapes.jsx";

// Custom frames: union / intersection of shapes and frames on the canvas.
// Each object's filled outline is traced to polygons in canvas coordinates
// (rotation, scale and flips included), combined with polygon-clipping, and
// the result becomes a frame whose mask is an SVG path normalised to its own
// bounding box (0–100 on both axes, so it touches all four edges — the rule
// fabric's path sizing and the server's stretched viewBox both rely on).
// The path holds only M/L/Z commands; it's drawn with fill-rule evenodd so
// holes from an intersection or union come out right.

const CURVE_STEPS = 14;
const ELLIPSE_STEPS = 96;
const CORNER_STEPS = 10;

const isPathCmd = (c) => Array.isArray(c) && typeof c[0] === "string";

// Rings in the object's own (unscaled, centre-origin) coordinates.
function localRings(obj) {
  if (obj.type === "ellipse" || obj.type === "circle") {
    const rx = obj.rx ?? obj.radius;
    const ry = obj.ry ?? obj.radius;
    const ring = [];
    for (let i = 0; i < ELLIPSE_STEPS; i++) {
      const t = (i / ELLIPSE_STEPS) * Math.PI * 2;
      ring.push([rx * Math.cos(t), ry * Math.sin(t)]);
    }
    return [ring];
  }
  if (obj.type === "polygon" || obj.type === "polyline") {
    const { x: ox, y: oy } = obj.pathOffset;
    return [obj.points.map((p) => [p.x - ox, p.y - oy])];
  }
  if (obj.type === "path") {
    const { x: ox, y: oy } = obj.pathOffset;
    const rings = [];
    let ring = null;
    let cur = [0, 0];
    let start = [0, 0];
    for (const cmd of obj.path.filter(isPathCmd)) {
      const [c, ...a] = cmd;
      if (c === "M") {
        if (ring && ring.length > 2) rings.push(ring);
        cur = [a[0], a[1]];
        start = cur;
        ring = [cur];
      } else if (c === "L") {
        cur = [a[0], a[1]];
        ring?.push(cur);
      } else if (c === "C") {
        const [x1, y1, x2, y2, x, y] = a;
        const [x0, y0] = cur;
        for (let i = 1; i <= CURVE_STEPS; i++) {
          const t = i / CURVE_STEPS;
          const u = 1 - t;
          ring?.push([
            u * u * u * x0 + 3 * u * u * t * x1 + 3 * u * t * t * x2 + t * t * t * x,
            u * u * u * y0 + 3 * u * u * t * y1 + 3 * u * t * t * y2 + t * t * t * y,
          ]);
        }
        cur = [x, y];
      } else if (c === "Q") {
        const [x1, y1, x, y] = a;
        const [x0, y0] = cur;
        for (let i = 1; i <= CURVE_STEPS; i++) {
          const t = i / CURVE_STEPS;
          const u = 1 - t;
          ring?.push([u * u * x0 + 2 * u * t * x1 + t * t * x, u * u * y0 + 2 * u * t * y1 + t * t * y]);
        }
        cur = [x, y];
      } else if (c === "Z" || c === "z") {
        cur = start;
      }
    }
    if (ring && ring.length > 2) rings.push(ring);
    return rings.map((r) => r.map(([x, y]) => [x - ox, y - oy]));
  }
  // rect (with optional rounded corners), and anything box-like (images).
  const w = obj.width;
  const h = obj.height;
  const rx = Math.min(obj.rx || 0, w / 2);
  const ry = Math.min(obj.ry || 0, h / 2);
  if (!rx || !ry) return [[[-w / 2, -h / 2], [w / 2, -h / 2], [w / 2, h / 2], [-w / 2, h / 2]]];
  const ring = [];
  const corners = [
    [w / 2 - rx, -h / 2 + ry, -Math.PI / 2],
    [w / 2 - rx, h / 2 - ry, 0],
    [-w / 2 + rx, h / 2 - ry, Math.PI / 2],
    [-w / 2 + rx, -h / 2 + ry, Math.PI],
  ];
  for (const [cx, cy, a0] of corners) {
    for (let i = 0; i <= CORNER_STEPS; i++) {
      const t = a0 + (i / CORNER_STEPS) * (Math.PI / 2);
      ring.push([cx + rx * Math.cos(t), cy + ry * Math.sin(t)]);
    }
  }
  return [ring];
}

function mapRings(rings, matrix) {
  return rings.map((ring) =>
    ring.map(([x, y]) => {
      const p = fabric.util.transformPoint(new fabric.Point(x, y), matrix);
      return [p.x, p.y];
    })
  );
}

// Objects that can take part: shapes, rectangles and images (a frame counts
// as its mask shape, a plain image as its box). Not text or groups.
export function isFrameOperand(obj) {
  return (
    obj &&
    !obj.isPageBackground &&
    !obj.isHoverOutline &&
    obj.shapeKind !== "grid" &&
    obj.shapeKind !== "dot_grid" &&
    (obj.layerType === "shape" || obj.layerType === "rect" || obj.layerType === "image")
  );
}

// The object's filled area as a polygon-clipping MultiPolygon, canvas coords.
function outlineOf(obj) {
  const matrix = obj.calcTransformMatrix();
  let rings;
  if (obj.layerType === "image" && obj.maskShape && obj.clipPath) {
    const clip = obj.clipPath;
    rings = mapRings(localRings(clip), fabric.util.multiplyTransformMatrices(matrix, clip.calcTransformMatrix()));
  } else {
    rings = mapRings(localRings(obj), matrix);
  }
  // Each subpath is its own polygon; union them into one shape.
  const polys = rings.filter((r) => r.length > 2).map((r) => [r]);
  if (!polys.length) return [];
  return polys.length === 1 ? [polys[0]] : polygonClipping.union(...polys);
}

const fmt = (n) => {
  const s = n.toFixed(2);
  return s.includes(".") ? s.replace(/\.?0+$/, "") : s;
};

// op: "union" | "intersection" | "difference" (the first object minus all
// the others — pass objects bottom-most first). Returns null when the result
// is empty (e.g. shapes that don't overlap), else { path, left, top, width,
// height } with left/top/width/height in canvas coordinates.
export function combineOutlines(objects, op) {
  const geoms = objects.map(outlineOf);
  if (geoms.filter((g) => g.length).length < 2) return null;
  let result;
  if (op === "intersection") result = polygonClipping.intersection(...geoms);
  else if (op === "difference") result = geoms[0].length ? polygonClipping.difference(geoms[0], ...geoms.slice(1)) : [];
  else result = polygonClipping.union(...geoms.filter((g) => g.length));
  if (!result.length) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  result.forEach((poly) =>
    poly.forEach((ring) =>
      ring.forEach(([x, y]) => {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      })
    )
  );
  const width = maxX - minX;
  const height = maxY - minY;
  if (width < 1 || height < 1) return null;

  const parts = [];
  result.forEach((poly) =>
    poly.forEach((ring) => {
      let last = null;
      const pts = [];
      // polygon-clipping repeats the first point at the end; Z closes it.
      ring.slice(0, -1).forEach(([x, y]) => {
        const p = `${fmt(((x - minX) / width) * 100)} ${fmt(((y - minY) / height) * 100)}`;
        if (p !== last) pts.push(p);
        last = p;
      });
      if (pts.length > 2) parts.push(`M${pts[0]}L${pts.slice(1).join(" ")}Z`);
    })
  );
  return { path: parts.join(""), left: minX, top: minY, width, height };
}

// Fabric clip shape for a custom frame path, in a width×height box.
export function customMaskShape(path, width, height) {
  const shape = new fabric.Path(path, { fill: "#000", fillRule: "evenodd" });
  shape.set({ scaleX: width / 100, scaleY: height / 100 });
  return shape;
}

// CSS mask (thumbnails) for a custom frame path; same SVG as the server's.
export function customMaskSvg(path) {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="none"><path d="${path}" fill-rule="evenodd"/></svg>`;
}

// ---- Corner rounding -------------------------------------------------------
// Rounds every sharp corner of a frame outline — convex and concave alike —
// by `radius` pixels at the frame's current size (width × height). Smooth
// stretches (sampled curves) are left alone. The result is baked into the
// path the server renders; the unrounded path is kept separately so the
// radius can be changed or re-applied after a resize.

const CORNER_TURN = (30 * Math.PI) / 180; // sharper than this = a corner
const FILLET_STEPS = 10;
// Zero-area subpaths at the box corners: keep the path's bounding box at
// exactly 0–100 even when rounding pulls a pointed tip inward, so fabric
// (sizes by the path's bbox) and the server (stretches the viewBox) agree.
const BOX_ANCHORS = "M0 0ZM100 100Z";

function parseRings(path) {
  return path
    .split("M")
    .map((part) => part.replace(/Z/g, "").trim())
    .filter(Boolean)
    .map((part) => {
      const nums = part.replace(/L/g, " ").trim().split(/\s+/).map(Number);
      const ring = [];
      for (let i = 0; i + 1 < nums.length; i += 2) ring.push([nums[i], nums[i + 1]]);
      return ring;
    })
    .filter((ring) => ring.length > 2);
}

function roundRing(pts, radius) {
  const n = pts.length;
  const seg = pts.map((p, i) => Math.hypot(pts[(i + 1) % n][0] - p[0], pts[(i + 1) % n][1] - p[1]));
  const cum = [0];
  for (let i = 0; i < n; i++) cum.push(cum[i] + seg[i]);
  const perimeter = cum[n];
  if (perimeter <= 0) return pts;

  const pointAt = (s) => {
    s = ((s % perimeter) + perimeter) % perimeter;
    let i = 0;
    while (i < n - 1 && cum[i + 1] < s) i++;
    const t = seg[i] ? (s - cum[i]) / seg[i] : 0;
    const a = pts[i];
    const b = pts[(i + 1) % n];
    return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
  };

  const corners = [];
  for (let i = 0; i < n; i++) {
    const a = pts[(i - 1 + n) % n];
    const p = pts[i];
    const b = pts[(i + 1) % n];
    const d1 = [p[0] - a[0], p[1] - a[1]];
    const d2 = [b[0] - p[0], b[1] - p[1]];
    const turn = Math.abs(Math.atan2(d1[0] * d2[1] - d1[1] * d2[0], d1[0] * d2[0] + d1[1] * d2[1]));
    if (turn > CORNER_TURN) corners.push({ i, s: cum[i], turn });
  }
  if (!corners.length) return pts;

  // Each fillet may use at most half the outline distance to its neighbours.
  corners.forEach((c, k) => {
    const prev = corners[(k - 1 + corners.length) % corners.length];
    const next = corners[(k + 1) % corners.length];
    const gapPrev = corners.length === 1 ? perimeter : (c.s - prev.s + perimeter) % perimeter || perimeter;
    const gapNext = corners.length === 1 ? perimeter : (next.s - c.s + perimeter) % perimeter || perimeter;
    const want = radius * Math.tan(Math.min(c.turn, Math.PI * 0.95) / 2);
    c.t = Math.min(want, gapPrev / 2, gapNext / 2);
  });

  const inWindow = (s) =>
    corners.some((c) => {
      const d = Math.abs(((s - c.s + perimeter * 1.5) % perimeter) - perimeter / 2);
      return d < c.t;
    });
  const events = [];
  pts.forEach((p, i) => {
    if (!inWindow(cum[i])) events.push({ key: cum[i], pts: [p] });
  });
  corners.forEach((c) => {
    const a = pointAt(c.s - c.t);
    const b = pointAt(c.s + c.t);
    const ctrl = pts[c.i];
    const arc = [];
    for (let k = 0; k <= FILLET_STEPS; k++) {
      const t = k / FILLET_STEPS;
      const u = 1 - t;
      arc.push([u * u * a[0] + 2 * u * t * ctrl[0] + t * t * b[0], u * u * a[1] + 2 * u * t * ctrl[1] + t * t * b[1]]);
    }
    events.push({ key: (((c.s - c.t) % perimeter) + perimeter) % perimeter, pts: arc });
  });
  events.sort((x, y) => x.key - y.key);
  return events.flatMap((e) => e.pts);
}

export function roundFramePath(path, width, height, radius) {
  const r = Math.max(0, radius || 0);
  if (!r || !width || !height) return path;
  const parts = parseRings(path).map((ring) => {
    const px = ring.map(([x, y]) => [(x / 100) * width, (y / 100) * height]);
    const rounded = roundRing(px, r).map(([x, y]) => `${fmt((x / width) * 100)} ${fmt((y / height) * 100)}`);
    return `M${rounded[0]}L${rounded.slice(1).join(" ")}Z`;
  });
  return parts.join("") + BOX_ANCHORS;
}

// A built-in frame shape (hexagon, star, …) as an outline path in the same
// 0–100 form as custom frames, so it can be rounded the same way.
export function kindOutlinePath(kind) {
  const shape = makeFabricShape(kind, { width: 100, height: 100, left: 0, top: 0 });
  const rings = mapRings(localRings(shape), shape.calcTransformMatrix());
  return rings
    .filter((r) => r.length > 2)
    .map((r) => `M${r.map(([x, y]) => `${fmt(Math.min(100, Math.max(0, x)))} ${fmt(Math.min(100, Math.max(0, y)))}`).join("L")}Z`)
    .join("");
}
